import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Plus, Trash2, ExternalLink, Globe, Pencil,
  Euro, Users as UsersIcon, CalendarDays, CalendarClock,
  Search, Download, MapPin, Package, TrendingUp, X,
} from "lucide-react";
import SalonMap from "@/components/SalonMap";
import DataResetDialog from "@/components/DataResetDialog";
import AdminLayout from "@/components/AdminLayout";

const SORT_OPTIONS = [
  { value: "revenue_desc", label: "Umsatz (höchster zuerst)" },
  { value: "revenue_asc", label: "Umsatz (niedrigster zuerst)" },
  { value: "customers_desc", label: "Kunden (meiste zuerst)" },
  { value: "bookings_desc", label: "Termine (meiste zuerst)" },
  { value: "name_asc", label: "Name (A–Z)" },
  { value: "created_desc", label: "Neueste zuerst" },
  { value: "created_asc", label: "Älteste zuerst" },
];

const FILTERS = [
  { value: "all", label: "Alle" },
  { value: "active_30d", label: "Aktiv (≤ 30 Tage)" },
  { value: "inactive_30d", label: "Inaktiv (> 30 Tage)" },
  { value: "never_used", label: "Nie genutzt" },
];

function daysSince(iso) {
  if (!iso) return Infinity;
  try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); } catch { return Infinity; }
}

function activityStatus(s) {
  const d = daysSince(s.stats?.last_activity);
  if (d === Infinity) return { label: "Nie genutzt", color: "#b91c1c" };
  if (d <= 30) return { label: `Aktiv (vor ${d} T.)`, color: "#8B9D83" };
  return { label: `Inaktiv (${d} T.)`, color: "#B07C2F" };
}

function subscriptionBadge(s) {
  const status = s.subscription_status;
  const cancel = s.subscription_cancel_at_period_end;
  const until = s.subscription_current_period_end || s.subscription_active_until;
  const trialEnd = s.subscription_trial_end;
  if (cancel && until) return { label: `Gekündigt zum ${new Date(until).toLocaleDateString("de-DE")}`, bg: "#fef3c7", fg: "#92400e" };
  if (status === "trialing") return { label: trialEnd ? `Trial bis ${new Date(trialEnd).toLocaleDateString("de-DE")}` : "Trial", bg: "#fef3c7", fg: "#92400e" };
  if (status === "active") return { label: until ? `Aktiv · nächste Zahlung ${new Date(until).toLocaleDateString("de-DE")}` : "Aktiv", bg: "rgba(34,197,94,0.15)", fg: "#15803d" };
  if (status === "past_due") return { label: "Zahlung fehlgeschlagen", bg: "rgba(220,38,38,0.15)", fg: "#b91c1c" };
  if (status === "canceled") return { label: "Abo gekündigt", bg: "#fee2e2", fg: "#b91c1c" };
  if (status === "free") return { label: "Free-Plan", bg: "#e7ebee", fg: "#5b6770" };
  return { label: "Kein Abo", bg: "#f3f4f6", fg: "#6b7280" };
}

export default function SuperAdmin() {
  const [salons, setSalons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [billingStats, setBillingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const emptyForm = {
    name: "", slug: "", owner_email: "", owner_password: "", owner_name: "",
    industry_id: "", plan_id: "",
    address: "", phone: "", email: "", description: "",
    geo_lat: "", geo_lng: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);  // null = create mode, else editing this salon id
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("revenue_desc");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, b, ind] = await Promise.all([
        api.get("/admin/salons"),
        api.get("/admin/plans"),
        api.get("/admin/billing-stats"),
        api.get("/admin/industries"),
      ]);
      setSalons(s.data);
      setPlans(p.data);
      setBillingStats(b.data || null);
      setIndustries(ind.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const assignPlan = async (salonId, planId) => {
    if (!planId) return;
    try {
      await api.put(`/admin/salons/${salonId}/plan`, { plan_id: planId });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Paket konnte nicht zugewiesen werden");
    }
  };

  const create = async (e) => {
    e.preventDefault(); setError(""); setCreating(true);
    try {
      if (editingId) {
        // Edit mode → PUT (no owner credentials)
        const payload = {
          name: form.name.trim(),
          slug: form.slug.trim().toLowerCase(),
          industry_id: form.industry_id || null,
          plan_id: form.plan_id || null,
          address: form.address.trim(),
          phone: form.phone.trim(),
          email: form.email.trim().toLowerCase() || null,
          description: form.description.trim(),
          geo_lat: form.geo_lat === "" ? null : parseFloat(form.geo_lat),
          geo_lng: form.geo_lng === "" ? null : parseFloat(form.geo_lng),
        };
        await api.put(`/admin/salons/${editingId}`, payload);
      } else {
        const payload = {
          name: form.name.trim(),
          slug: form.slug.trim().toLowerCase(),
          owner_name: form.owner_name.trim(),
          owner_email: form.owner_email.trim().toLowerCase(),
          owner_password: form.owner_password,
          industry_id: form.industry_id || null,
          plan_id: form.plan_id || null,
          address: form.address.trim() || "",
          phone: form.phone.trim() || "",
          email: form.email.trim().toLowerCase() || null,
          description: form.description.trim() || "",
          geo_lat: form.geo_lat === "" ? null : parseFloat(form.geo_lat),
          geo_lng: form.geo_lng === "" ? null : parseFloat(form.geo_lng),
        };
        await api.post("/admin/salons", payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setError("");
    setForm({
      name: s.name || "",
      slug: s.slug || "",
      owner_email: "", owner_password: "", owner_name: "",  // not editable here
      industry_id: s.industry_id || "",
      plan_id: s.plan_id || "",
      address: s.address || "",
      phone: s.phone || "",
      email: s.email || "",
      description: s.description || "",
      geo_lat: s.geo_lat ?? "",
      geo_lng: s.geo_lng ?? "",
    });
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError("");
    setForm(emptyForm);
  };

  const [geocoding, setGeocoding] = useState(false);
  const fetchGeo = async () => {
    if (!form.address.trim()) {
      setError("Bitte zuerst eine Adresse eingeben"); return;
    }
    setError(""); setGeocoding(true);
    try {
      const { data } = await api.post("/admin/geocode", { address: form.address.trim() });
      setForm({ ...form, geo_lat: data.lat, geo_lng: data.lng });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Geocoding fehlgeschlagen");
    } finally { setGeocoding(false); }
  };

  const [pwResult, setPwResult] = useState(null);  // { owner_email, new_password }
  const [resetting, setResetting] = useState(false);
  const resetOwnerPassword = async () => {
    if (!editingId) return;
    if (!window.confirm("Owner-Passwort wirklich zurücksetzen? Das alte Passwort wird sofort ungültig.")) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/salons/${editingId}/reset-owner-password`);
      setPwResult(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Passwort-Reset fehlgeschlagen");
    } finally { setResetting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Salon wirklich löschen? Alle Daten gehen verloren.")) return;
    await api.delete(`/admin/salons/${id}`);
    load();
  };

  const [resetTarget, setResetTarget] = useState(null);   // {id, name} or null

  const visible = useMemo(() => {
    let xs = [...salons];
    const q = search.trim().toLowerCase();
    if (q) xs = xs.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.slug || "").toLowerCase().includes(q) || (s.address || "").toLowerCase().includes(q));
    if (filter === "active_30d") xs = xs.filter((s) => daysSince(s.stats?.last_activity) <= 30);
    if (filter === "inactive_30d") xs = xs.filter((s) => { const d = daysSince(s.stats?.last_activity); return d !== Infinity && d > 30; });
    if (filter === "never_used") xs = xs.filter((s) => !s.stats?.last_activity);
    const get = (s, k) => (s.stats || {})[k] || 0;
    xs.sort((a, b) => {
      switch (sort) {
        case "revenue_desc": return get(b, "total_revenue") - get(a, "total_revenue");
        case "revenue_asc": return get(a, "total_revenue") - get(b, "total_revenue");
        case "customers_desc": return get(b, "total_customers") - get(a, "total_customers");
        case "bookings_desc": return get(b, "total_bookings") - get(a, "total_bookings");
        case "name_asc": return (a.name || "").localeCompare(b.name || "");
        case "created_desc": return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case "created_asc": return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default: return 0;
      }
    });
    return xs;
  }, [salons, search, filter, sort]);

  const totals = useMemo(() => visible.reduce((acc, s) => ({
    revenue: acc.revenue + (s.stats?.total_revenue || 0),
    customers: acc.customers + (s.stats?.total_customers || 0),
    bookings: acc.bookings + (s.stats?.total_bookings || 0),
  }), { revenue: 0, customers: 0, bookings: 0 }), [visible]);

  const exportCsv = () => {
    const rows = [["Name", "Slug", "Subdomain", "Adresse", "Registriert", "Letzte Aktivität", "Umsatz (€)", "Kunden", "Termine"]];
    visible.forEach((s) => {
      rows.push([
        s.name, s.slug, s.subdomain || "", s.address || "",
        s.created_at ? new Date(s.created_at).toISOString().slice(0, 10) : "",
        s.stats?.last_activity ? new Date(s.stats.last_activity).toISOString().slice(0, 10) : "",
        (s.stats?.total_revenue || 0).toFixed(2).replace(".", ","),
        s.stats?.total_customers || 0, s.stats?.total_bookings || 0,
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `studiomv_salons_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {showForm && (
          <form onSubmit={create} className="mv-card p-6 mb-10 mv-fade-in" data-testid="salon-form">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="mv-label mb-1">{editingId ? "Salon bearbeiten" : "Mandant manuell anlegen"}</p>
                <h2 className="font-display text-2xl">{editingId ? form.name || "Salon" : "Neuer Salon"}</h2>
                <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                  {editingId
                    ? "Aktualisiere Stammdaten, Berufsgruppe, Paket und Kontaktinfos. Owner-Login bleibt unverändert."
                    : "Pflichtfelder: Name, Slug, Inhaber-Daten. Alle weiteren Angaben sind optional und können auch später im CMS ergänzt werden."}
                </p>
              </div>
              <button type="button" onClick={cancelForm} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]" disabled={creating}>
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            {error && (
              <p className="text-sm mb-4 px-3 py-2 rounded bg-red-50 text-red-700" data-testid="salon-form-error">{error}</p>
            )}

            {/* Section 1 – essentials */}
            <p className="mv-label mb-3">Stammdaten</p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs mb-1">Salon-Name *</label>
                <input data-testid="salon-name-input" required className="mv-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs mb-1">URL-Slug *</label>
                <input data-testid="salon-slug-input" required className="mv-input font-mono" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="bella-capelli" />
              </div>
              <div>
                <label className="block text-xs mb-1">Berufsgruppe</label>
                <select data-testid="salon-industry-select" className="mv-input" value={form.industry_id} onChange={(e) => setForm({ ...form, industry_id: e.target.value })}>
                  <option value="">(Standard: Friseur)</option>
                  {industries.filter((i) => i.active).map((i) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1">Paket</label>
                <select data-testid="salon-plan-input" className="mv-input" value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}>
                  <option value="">(später zuweisen)</option>
                  {plans.filter((p) => p.active).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.on_sale ? `${p.sale_price.toFixed(0)} €` : `${p.monthly_price.toFixed(0)} €`}/Monat
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Section 2 – owner (create only) */}
            {!editingId && (
              <>
                <p className="mv-label mb-3">Inhaber-Konto</p>
                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-xs mb-1">Name *</label>
                    <input data-testid="salon-owner_name-input" required className="mv-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">E-Mail (Login) *</label>
                    <input data-testid="salon-owner_email-input" required type="email" className="mv-input" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Passwort *</label>
                    <input data-testid="salon-owner_password-input" required type="password" minLength={8} className="mv-input" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} placeholder="mindestens 8 Zeichen" />
                  </div>
                </div>
              </>
            )}

            {/* Section 3 – contact + address */}
            <p className="mv-label mb-3">Kontakt & Standort (optional)</p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs mb-1">Öffentliche E-Mail</label>
                <input data-testid="salon-contact-email-input" type="email" className="mv-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="meinsalon@studiomv.de" />
              </div>
              <div>
                <label className="block text-xs mb-1">Telefon</label>
                <input data-testid="salon-phone-input" className="mv-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+49 30 12345678" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Adresse</label>
                <div className="flex gap-2">
                  <input data-testid="salon-address-input" className="mv-input flex-1" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Straße Hausnr, PLZ Ort" />
                  <button type="button" data-testid="salon-fetch-geo-btn" onClick={fetchGeo} disabled={geocoding || !form.address.trim()} className="mv-btn-ghost text-sm whitespace-nowrap">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} /> {geocoding ? "Lade…" : "Geo aus Adresse"}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1">Geo-Lat (für Karte)</label>
                <input data-testid="salon-geo-lat-input" type="number" step="any" className="mv-input font-mono text-sm" value={form.geo_lat} onChange={(e) => setForm({ ...form, geo_lat: e.target.value })} placeholder="52.5200" />
              </div>
              <div>
                <label className="block text-xs mb-1">Geo-Lng (für Karte)</label>
                <input data-testid="salon-geo-lng-input" type="number" step="any" className="mv-input font-mono text-sm" value={form.geo_lng} onChange={(e) => setForm({ ...form, geo_lng: e.target.value })} placeholder="13.4050" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs mb-1">Kurzbeschreibung</label>
                <textarea data-testid="salon-description-input" className="mv-input min-h-[60px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Kurze öffentliche Beschreibung des Salons" />
              </div>
            </div>

            {editingId && (
              <div className="mb-4 p-4 rounded-lg" style={{ background: "var(--mv-surface)" }} data-testid="owner-pw-reset-row">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-medium">Owner-Passwort zurücksetzen</p>
                    <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                      Generiert ein neues 12-stelliges Passwort für den Inhaber. Das alte wird sofort ungültig.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="owner-pw-reset-btn"
                    onClick={resetOwnerPassword}
                    disabled={resetting}
                    className="mv-btn-ghost text-sm"
                  >
                    {resetting ? "Setze zurück…" : "Passwort zurücksetzen"}
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4 border-t" style={{ borderColor: "var(--mv-border)" }}>
              <button type="button" onClick={cancelForm} className="mv-btn-ghost" disabled={creating}>Abbrechen</button>
              <button data-testid="salon-create-submit" className="mv-btn" disabled={creating}>
                {creating ? "Speichere…" : (editingId ? "Änderungen speichern" : "Salon anlegen")}
              </button>
            </div>
          </form>
        )}

        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="mv-label mb-2">Mandanten</p>
            <h1 className="font-display text-4xl">Alle Salons</h1>
            <p className="text-sm mt-2" style={{ color: "var(--mv-text-muted)" }}>{salons.length} Salons auf der Plattform</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="export-csv-btn" onClick={exportCsv} className="mv-btn-ghost" title="Als CSV exportieren">
              <Download className="w-4 h-4" strokeWidth={1.5} /> CSV
            </button>
            <button data-testid="new-salon-btn" onClick={() => { setEditingId(null); setForm(emptyForm); setShowForm(true); setError(""); setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50); }} className="mv-btn">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> Neuer Salon
            </button>
          </div>
        </div>

        {!loading && salons.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="platform-totals">
            <div className="mv-card p-5">
              <div className="flex items-center justify-between mb-2"><Globe className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} /><p className="mv-label">Salons</p></div>
              <p className="font-display text-3xl">{visible.length}</p>
            </div>
            <div className="mv-card p-5">
              <div className="flex items-center justify-between mb-2"><Euro className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-secondary)" }} /><p className="mv-label">Umsatz</p></div>
              <p className="font-display text-3xl">{totals.revenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
            </div>
            <div className="mv-card p-5">
              <div className="flex items-center justify-between mb-2"><UsersIcon className="w-4 h-4" strokeWidth={1.5} /><p className="mv-label">Kunden</p></div>
              <p className="font-display text-3xl">{totals.customers.toLocaleString("de-DE")}</p>
            </div>
            <div className="mv-card p-5">
              <div className="flex items-center justify-between mb-2"><CalendarDays className="w-4 h-4" strokeWidth={1.5} /><p className="mv-label">Termine</p></div>
              <p className="font-display text-3xl">{totals.bookings.toLocaleString("de-DE")}</p>
            </div>
          </div>
        )}

        {!loading && billingStats && (
          <div className="mb-10" data-testid="billing-stats">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <p className="mv-label">Abos & Einnahmen</p>
            </div>
            <div className="grid lg:grid-cols-[1fr_1fr_2fr] gap-4">
              <div className="mv-card p-5">
                <p className="mv-label mb-2">Aktive Abos</p>
                <p className="font-display text-3xl" data-testid="stat-active-subs">{billingStats.active_subscriptions}</p>
                <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                  {billingStats.trialing > 0 && <>{billingStats.trialing} im Trial · </>}
                  {billingStats.canceling > 0 && <>{billingStats.canceling} kündigend</>}
                  {billingStats.trialing === 0 && billingStats.canceling === 0 && "alle zahlend"}
                </p>
              </div>
              <div className="mv-card p-5" style={{ background: "var(--mv-surface)" }}>
                <p className="mv-label mb-2">MRR (monatlich wiederkehrend)</p>
                <p className="font-display text-3xl" style={{ color: "var(--mv-primary)" }} data-testid="stat-mrr">
                  {billingStats.monthly_recurring_revenue.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                </p>
                <p className="text-xs mt-1" style={{ color: "var(--mv-text-muted)" }}>
                  Hochrechnung: {(billingStats.monthly_recurring_revenue * 12).toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} € / Jahr
                </p>
              </div>
              <div className="mv-card p-5" data-testid="top-plans-table">
                <p className="mv-label mb-3">Top 5 Pakete</p>
                {billingStats.top_plans.length === 0 ? (
                  <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Noch keine Buchungen</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs" style={{ color: "var(--mv-text-muted)" }}>
                        <th className="pb-2">Paket</th>
                        <th className="pb-2 text-right">Salons</th>
                        <th className="pb-2 text-right">Aktiv</th>
                        <th className="pb-2 text-right">MRR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingStats.top_plans.map((p) => (
                        <tr key={p.plan_id} className="border-t" style={{ borderColor: "var(--mv-border)" }}>
                          <td className="py-1.5">
                            <span className="font-medium">{p.plan_name}</span>{" "}
                            <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>· {p.monthly_price.toFixed(0)} €</span>
                          </td>
                          <td className="py-1.5 text-right font-medium">{p.salon_count}</td>
                          <td className="py-1.5 text-right" style={{ color: p.active_count > 0 ? "#15803d" : "var(--mv-text-muted)" }}>{p.active_count}</td>
                          <td className="py-1.5 text-right font-medium">{p.mrr.toFixed(2)} €</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && salons.length > 0 && (
          <div className="mb-8" data-testid="salon-map-wrapper">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
              <p className="mv-label">Standorte ({visible.filter((s) => s.geo_lat).length}/{visible.length})</p>
            </div>
            <SalonMap salons={visible.filter((s) => s.geo_lat && s.geo_lng)} />
          </div>
        )}

        {!loading && salons.length > 0 && (
          <div className="mv-card p-3 md:p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="salon-filters">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
              <input
                data-testid="salon-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suche nach Name, Slug oder Adresse"
                className="mv-input pl-9 text-sm"
              />
            </div>
            <select data-testid="salon-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="mv-input text-sm w-auto">
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select data-testid="salon-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="mv-input text-sm w-auto">
              {FILTERS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}

        {loading ? (
          <p style={{ color: "var(--mv-text-muted)" }}>Lade...</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-center py-12" style={{ color: "var(--mv-text-muted)" }}>Keine Salons passen zum Filter.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((s) => {
              const st = s.stats || {};
              const created = s.created_at ? new Date(s.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
              const act = activityStatus(s);
              return (
                <div key={s.id} className="mv-card p-6" data-testid={`salon-card-${s.slug}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-xl truncate">{s.name}</h3>
                      <p className="text-xs flex items-center gap-1 mt-1 truncate" style={{ color: "var(--mv-text-muted)" }}>
                        <Globe className="w-3 h-3" strokeWidth={1.5} /> {s.subdomain}
                      </p>
                      <p className="text-xs flex items-center gap-1 mt-1" style={{ color: "var(--mv-text-muted)" }}>
                        <CalendarClock className="w-3 h-3" strokeWidth={1.5} /> Registriert {created}
                      </p>
                      {s.address && (
                        <p className="text-xs flex items-center gap-1 mt-1 truncate" style={{ color: "var(--mv-text-muted)" }}>
                          <MapPin className="w-3 h-3" strokeWidth={1.5} /> {s.address}
                        </p>
                      )}
                      <p className="text-xs mt-2" style={{ color: act.color }}>● {act.label}</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button data-testid={`edit-salon-${s.slug}`} onClick={() => startEdit(s)} className="p-2 rounded-md hover:bg-[var(--mv-surface)]" title="Bearbeiten">
                        <Pencil className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
                      </button>
                      <button data-testid={`delete-salon-${s.slug}`} onClick={() => remove(s.id)} className="p-2 rounded-md hover:bg-[var(--mv-surface)]" title="Löschen">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-5 mb-4" data-testid={`salon-stats-${s.slug}`}>
                    <div className="text-center p-3 rounded-xl" style={{ background: "var(--mv-surface)" }}>
                      <p className="font-display text-base leading-tight" data-testid={`stat-revenue-${s.slug}`}>{(st.total_revenue || 0).toLocaleString("de-DE", { maximumFractionDigits: 0 })} €</p>
                      <p className="text-[10px] mt-1" style={{ color: "var(--mv-text-muted)" }}>Umsatz</p>
                    </div>
                    <div className="text-center p-3 rounded-xl" style={{ background: "var(--mv-surface)" }}>
                      <p className="font-display text-base leading-tight" data-testid={`stat-customers-${s.slug}`}>{(st.total_customers || 0).toLocaleString("de-DE")}</p>
                      <p className="text-[10px] mt-1" style={{ color: "var(--mv-text-muted)" }}>Kunden</p>
                    </div>
                    <div className="text-center p-3 rounded-xl" style={{ background: "var(--mv-surface)" }}>
                      <p className="font-display text-base leading-tight" data-testid={`stat-bookings-${s.slug}`}>{(st.total_bookings || 0).toLocaleString("de-DE")}</p>
                      <p className="text-[10px] mt-1" style={{ color: "var(--mv-text-muted)" }}>Termine</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3" data-testid={`salon-plan-${s.slug}`}>
                    <Package className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} style={{ color: "var(--mv-text-muted)" }} />
                    <select
                      data-testid={`salon-plan-select-${s.slug}`}
                      value={s.plan_id || ""}
                      onChange={(e) => assignPlan(s.id, e.target.value)}
                      className="mv-input text-xs py-1 flex-1"
                    >
                      <option value="" disabled>— Paket wählen —</option>
                      {plans.filter((p) => p.active).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} · {p.on_sale ? `${p.sale_price.toFixed(0)} € (statt ${p.monthly_price.toFixed(0)} €)` : `${p.monthly_price.toFixed(0)} €`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {(() => {
                    const b = subscriptionBadge(s);
                    return (
                      <p
                        data-testid={`sub-badge-${s.slug}`}
                        className="text-[11px] px-2 py-1 rounded mb-3 inline-block w-full"
                        style={{ background: b.bg, color: b.fg }}
                      >
                        {b.label}
                      </p>
                    );
                  })()}

                  <div className="flex gap-2">
                    <a href={`/salon/${s.slug}`} target="_blank" rel="noreferrer" className="mv-btn-ghost text-xs flex-1 justify-center" data-testid={`view-public-${s.slug}`}>
                      <ExternalLink className="w-3 h-3" strokeWidth={1.5} /> Vorschau
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {pwResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(44,38,33,0.5)" }} onClick={() => setPwResult(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="owner-pw-result-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="mv-label mb-1">Passwort zurückgesetzt</p>
                <h2 className="font-display text-xl">Neue Zugangsdaten</h2>
              </div>
              <button onClick={() => setPwResult(null)} className="p-1.5 -m-1 rounded hover:bg-[var(--mv-surface)]">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--mv-text-muted)" }}>
              Notiere oder kopiere das Passwort jetzt — es wird nur einmal angezeigt und kann später nicht erneut abgerufen werden.
            </p>
            <div className="space-y-3">
              <div>
                <p className="text-[11px] mb-1" style={{ color: "var(--mv-text-muted)" }}>E-Mail (Login)</p>
                <p className="mv-input font-mono text-sm bg-[var(--mv-surface)]">{pwResult.owner_email}</p>
              </div>
              <div>
                <p className="text-[11px] mb-1" style={{ color: "var(--mv-text-muted)" }}>Neues Passwort</p>
                <div className="flex gap-2">
                  <p data-testid="owner-pw-new" className="mv-input font-mono text-sm bg-[var(--mv-surface)] flex-1">{pwResult.new_password}</p>
                  <button
                    type="button"
                    data-testid="owner-pw-copy"
                    onClick={() => navigator.clipboard?.writeText(pwResult.new_password)}
                    className="mv-btn-ghost text-sm whitespace-nowrap"
                  >
                    Kopieren
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={() => setPwResult(null)} className="mv-btn">Verstanden</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
